// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — FlowViewer: Read-only flowchart viewer
// Prompt 140: single 32px bar, correct phases key, fitToCanvas
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { useI18n } from '../../../../../i18n';
import { FLOW_TOKENS as T } from '../shared/flowTokens';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { formatDate } from '../../../../../lib/design';
import { StatusBadge } from '../../../../../lib/ui';
import type { EstadoDocumento } from '../../../../../types';

import type { Flujograma, FlowNode, FlowEdge, FlowPhase } from '../types/flowchart.types';

import FlowViewerCanvas from './FlowViewerCanvas';
import { request } from '../../../../shared/services/api';

async function fetchPublished(procesoId: string): Promise<Flujograma | null> {
  try {
    const body = await request<{ data?: Flujograma }>(`/flujogramas/${procesoId}/published`);
    return body.data ?? null;
  } catch {
    return null;
  }
}

interface FlowViewerProps {
  procesoCodigo?: string;
  procesoNombre?: string;
  procesoId: string;
  onAbrirEnEditor?: () => void;
  hideBar?: boolean;
  flujograma?: Flujograma | null;
  onNavigateDocument?: (codigo: string) => void;
  onNavigateProcess?: (codigo: string) => void;
}

// ─── Viewer Controls (with fitToCanvas) ─────────────────────────────────────

function ViewerControls({ onFitToCanvas }: { onFitToCanvas: () => void }) {
  const { zoomIn, zoomOut } = useReactFlow();
  const btn: React.CSSProperties = {
    fontSize: '8.5px',
    fontFamily: T.font.family,
    fontWeight: 500,
    padding: '2px 6px',
    border: `1px solid ${T.color.border}`,
    borderRadius: 3,
    background: T.color.surface,
    color: T.color.textSecondary,
    cursor: 'pointer',
    lineHeight: 1.2,
  };
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      <button style={btn} onClick={() => zoomOut()} title="Alejar">
        {'\u2212'}
      </button>
      <button style={btn} onClick={() => zoomIn()} title="Acercar">
        +
      </button>
      <button style={btn} onClick={onFitToCanvas} title="Ajustar A3">
        {'\u229F'}
      </button>
    </div>
  );
}

// ─── Inner Viewer ───────────────────────────────────────────────────────────

function FlowViewerInner({
  procesoCodigo,
  procesoNombre,
  procesoId,
  onAbrirEnEditor,
  hideBar,
  flujograma,
  nodes,
  edges,
  phases,
  onNavigateDocument,
  onNavigateProcess,
}: {
  procesoCodigo?: string;
  procesoNombre?: string;
  procesoId: string;
  onAbrirEnEditor?: () => void;
  hideBar?: boolean;
  flujograma: Flujograma;
  nodes: FlowNode[];
  edges: FlowEdge[];
  phases: FlowPhase[];
  onNavigateDocument?: (c: string) => void;
  onNavigateProcess?: (c: string) => void;
}) {
  const { setViewport } = useReactFlow();
  const isDraft = flujograma.estado !== 'PUBLICADO';

  const fitToCanvas = useCallback(() => {
    const el = document.querySelector('.react-flow');
    if (!el) return;
    const zoom = el.clientWidth / T.canvas.a3Width;
    setViewport({ x: 0, y: 20, zoom }, { duration: 200 });
  }, [setViewport]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        fontFamily: T.font.family,
        position: 'relative',
      }}
    >
      {/* Barra unica del visor — 32px (oculta con hideBar) */}
      {!hideBar && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            height: 32,
            minHeight: 32,
            flexShrink: 0,
            borderBottom: `1px solid ${T.color.border}`,
            backgroundColor: T.color.surface,
            padding: '0 10px',
            fontSize: 11,
          }}
        >
          <span
            style={{
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: 10,
              color: T.color.navy,
              marginRight: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {procesoCodigo ?? flujograma.procesoId}
          </span>
          {procesoNombre && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: T.color.textPrimary,
                marginRight: 8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 280,
              }}
            >
              {procesoNombre}
            </span>
          )}
          <StatusBadge status={flujograma.estado as EstadoDocumento} small />
          <span
            style={{
              fontSize: 9,
              fontWeight: 500,
              color: T.color.textSecondary,
              marginLeft: 6,
              whiteSpace: 'nowrap',
            }}
          >
            Ed. {String(flujograma.version).padStart(2, '0')}
          </span>
          {flujograma.fechaPublicacion && (
            <span style={{ fontSize: 9, color: T.color.textTertiary, marginLeft: 8 }}>
              {formatDate(flujograma.fechaPublicacion)}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {onAbrirEnEditor && (
            <button
              onClick={onAbrirEnEditor}
              style={{
                fontSize: 10,
                color: (T.color as Record<string, string>).accent ?? '#1A94BF',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: T.font.family,
                fontWeight: 600,
                marginRight: 8,
                whiteSpace: 'nowrap',
              }}
            >
              Abrir en editor {'\u2197'}
            </button>
          )}
          <ViewerControls onFitToCanvas={fitToCanvas} />
        </div>
      )}

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {isDraft && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <span
              style={{
                fontSize: 70,
                fontWeight: 900,
                fontFamily: T.font.family,
                color: T.color.navy,
                opacity: 0.08,
                transform: 'rotate(-30deg)',
                userSelect: 'none',
                letterSpacing: 8,
              }}
            >
              BORRADOR
            </span>
          </div>
        )}
        <FlowViewerCanvas
          nodes={nodes}
          edges={edges}
          phases={phases}
          onFitToCanvas={fitToCanvas}
          onInit={(fit) => fit()}
          onNavigateDocument={onNavigateDocument}
          onNavigateProcess={onNavigateProcess}
          procesoId={procesoId}
        />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FlowViewer({
  procesoId,
  procesoCodigo,
  procesoNombre,
  onAbrirEnEditor,
  hideBar,
  flujograma: externalFlujograma,
  onNavigateDocument,
  onNavigateProcess,
}: FlowViewerProps) {
  const { t } = useI18n();
  const [flujograma, setFlujograma] = useState<Flujograma | null>(externalFlujograma ?? null);
  const [loading, setLoading] = useState(!externalFlujograma);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (externalFlujograma !== undefined) {
      setFlujograma(externalFlujograma);
      setLoading(false);
      return;
    }
    if (!procesoId) return;
    setLoading(true);
    setError(null);
    fetchPublished(procesoId)
      .then((data) => {
        setFlujograma(data);
        if (!data) setError(null);
      })
      .catch((err) => setError(err.message ?? 'Error al cargar flujograma'))
      .finally(() => setLoading(false));
  }, [procesoId, externalFlujograma]);

  if (loading)
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: T.color.surfaceSubtle,
        }}
      >
        <LoadingSpinner text={t.flow.cargandoFlujograma} size="sm" />
      </div>
    );

  if (error)
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: T.color.surfaceSubtle,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: T.color.statusRejected, marginBottom: 8 }}>{error}</div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '4px 12px',
              fontSize: '8.5px',
              fontFamily: T.font.family,
              backgroundColor: T.color.surface,
              border: `1px solid ${T.color.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              color: T.color.textSecondary,
            }}
          >
            {t.flow.reintentar}
          </button>
        </div>
      </div>
    );

  if (!flujograma || !flujograma.contenido)
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: T.color.surfaceSubtle,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <svg
            width={48}
            height={48}
            viewBox="0 0 24 24"
            fill="none"
            stroke={T.color.border}
            strokeWidth={1.5}
            style={{ marginBottom: 12 }}
          >
            <rect x={3} y={3} width={7} height={7} rx={1} />
            <rect x={14} y={3} width={7} height={7} rx={1} />
            <rect x={8} y={14} width={7} height={7} rx={1} />
            <path d="M6.5 10v2.5a1 1 0 001 1H11M17.5 10v2.5a1 1 0 01-1 1H13" />
          </svg>
          <p style={{ fontSize: 9, fontFamily: T.font.family, color: T.color.textTertiary }}>
            {t.flow.sinFlujogramaPublicado}
          </p>
          <p
            style={{
              fontSize: 8,
              fontFamily: T.font.family,
              color: T.color.textTertiary,
              marginTop: 4,
              opacity: 0.7,
            }}
          >
            {t.flow.crearDesdeEditor}
          </p>
        </div>
      </div>
    );

  // F4 fix: use metadata.phases (not metadata.fases) — phases already have correct widths
  const nodes: FlowNode[] = flujograma.contenido.nodes ?? [];
  const edges: FlowEdge[] = flujograma.contenido.edges ?? [];
  const phases: FlowPhase[] = (flujograma.contenido.metadata?.phases ?? []) as FlowPhase[];

  return (
    <ReactFlowProvider>
      <FlowViewerInner
        procesoCodigo={procesoCodigo}
        procesoNombre={procesoNombre}
        procesoId={procesoId}
        onAbrirEnEditor={onAbrirEnEditor}
        hideBar={hideBar}
        flujograma={flujograma}
        nodes={nodes}
        edges={edges}
        phases={phases}
        onNavigateDocument={onNavigateDocument}
        onNavigateProcess={onNavigateProcess}
      />
    </ReactFlowProvider>
  );
}
